import { useMemo } from 'react';
import { siteColor } from './siteGraph';
import type { Segment } from './siteGraph';
import type { SegmentEdge } from '../../types';

/**
 * One level down: the actual pages behind an arrow.
 *
 * TWO COLUMNS, NOT A RING. A segment is bipartite by construction — sources on
 * one site, targets on another — and a layout that says so lets you read every
 * arrow's direction without following it. A circle would make the reader trace
 * each line to find out which end was which.
 *
 * A page appears ONCE per column even when it has several links, so fan-out and
 * fan-in are visible as shape: one source with six arrows leaving it is a hub,
 * and six sources converging on one target is the pattern you built the map to
 * create.
 *
 * Self-segments (a site linking within itself) put the same page on both sides.
 * That is honest rather than clever — the alternative is a single column with
 * curved self-edges, which is exactly the hairball this view avoids.
 */

const ROW = 30;
const PAD = 18;
const COL_W = 250;
const GAP = 150;

interface Props {
  segment: Segment;
  edges: SegmentEdge[];
  onOpenPage?: (pageId: number) => void;
}

export default function SegmentMap({ segment, edges, onOpenPage }: Props) {
  const { sources, targets, height } = useMemo(() => {
    const s = new Map<number, string>();
    const t = new Map<number, string>();
    for (const e of edges) {
      if (!s.has(e.page_id)) s.set(e.page_id, e.source_title);
      if (!t.has(e.linked_page_id)) t.set(e.linked_page_id, e.target_title);
    }
    const toRows = (m: Map<number, string>) =>
      Array.from(m.entries()).map(([id, title], i) => ({
        id,
        title,
        y: PAD + i * ROW,
      }));
    const src = toRows(s);
    const tgt = toRows(t);
    return {
      sources: src,
      targets: tgt,
      height: PAD * 2 + Math.max(src.length, tgt.length) * ROW,
    };
  }, [edges]);

  const yOf = (rows: { id: number; y: number }[], id: number) =>
    rows.find((r) => r.id === id)?.y ?? 0;

  const width = COL_W * 2 + GAP;
  const fromColor = siteColor(0);
  const toColor = siteColor(1);

  if (!edges.length) return null;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ maxHeight: Math.min(height, 420) }}
      className="w-full"
      role="img"
      aria-label={`Pages linking from ${segment.fromName} to ${segment.toName}`}
    >
      <defs>
        <marker
          id="seg-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill={toColor} />
        </marker>
      </defs>

      {edges.map((e) => {
        const y1 = yOf(sources, e.page_id);
        const y2 = yOf(targets, e.linked_page_id);
        const x1 = COL_W;
        const x2 = COL_W + GAP;
        // A flat S-curve: straight lines between two columns overlap into a
        // moiré as soon as there are more than a handful.
        return (
          <path
            key={e.id}
            d={`M${x1},${y1} C${x1 + GAP / 2},${y1} ${x2 - GAP / 2},${y2} ${x2},${y2}`}
            fill="none"
            stroke={toColor}
            strokeWidth={1.25}
            opacity={0.5}
            markerEnd="url(#seg-arrow)"
          />
        );
      })}

      {sources.map((row) => (
        <g
          key={`s-${row.id}`}
          className="cursor-pointer"
          onClick={() => onOpenPage?.(row.id)}
        >
          <circle cx={COL_W - 4} cy={row.y} r={3.5} fill={fromColor} />
          <text
            x={COL_W - 13}
            y={row.y}
            textAnchor="end"
            dy="0.34em"
            fontSize={11}
            className="fill-gray-700 dark:fill-gray-200 hover:fill-blue-600"
          >
            {clip(row.title)}
          </text>
        </g>
      ))}

      {targets.map((row) => (
        <g
          key={`t-${row.id}`}
          className="cursor-pointer"
          onClick={() => onOpenPage?.(row.id)}
        >
          <circle cx={COL_W + GAP + 4} cy={row.y} r={3.5} fill={toColor} />
          <text
            x={COL_W + GAP + 13}
            y={row.y}
            dy="0.34em"
            fontSize={11}
            className="fill-gray-700 dark:fill-gray-200 hover:fill-blue-600"
          >
            {clip(row.title)}
          </text>
        </g>
      ))}
    </svg>
  );
}

/** SVG has no text-overflow, so a long title would run under the next column
 *  and overlap the arrows rather than being clipped. */
function clip(title: string): string {
  return title.length > 34 ? `${title.slice(0, 33)}…` : title;
}
